import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PermissionService } from './permission.service';
import { PermissionNames } from 'src/app/modules/Settings/permissions/enum/permission-names.enum';

export interface IMenuItem {
    id?: string;
    title?: string;
    description?: string;
    type: string;
    name?: string;
    state?: string;
    icon?: string;
    permission?: string;
    tooltip?: string;
    disabled?: boolean;
    sub?: IChildItem[];
    badges?: IBadge[];
    active?: boolean;
}

export interface IChildItem {
    id?: string;
    parentId?: string;
    type?: string;
    permission?: string;
    name: string;
    state?: string;
    icon?: string;
    sub?: IChildItem[];
    active?: boolean;
}

interface IBadge {
    color: string;
    value: string;
}

interface ISidebarState {
    sidenavOpen?: boolean;
    childnavOpen?: boolean;
    sideIcon?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class NavigationService {
    public sidebarState: ISidebarState = {
        sidenavOpen: true,
        childnavOpen: false,
        sideIcon: true,
    };

    selectedItem: IMenuItem;
    menuItems$: Observable<IMenuItem[]>;
    menuItems: BehaviorSubject<IMenuItem[]>;
    defaultMenu: IMenuItem[];

    constructor(private permissionService: PermissionService) {
        console.log('NavigationService constructor called');

        // Initialize default menu structure
        this.initializeDefaultMenu();

        // Initialize with empty menu first
        this.menuItems = new BehaviorSubject<IMenuItem[]>([]);
        this.menuItems$ = this.menuItems.asObservable();

        // Set up permissions subscription
        this.setupPermissionSubscription();
    }

    private setupPermissionSubscription() {
        console.log('Setting up permission subscription');

        this.permissionService.permissionsReady$.subscribe((isReady) => {
            console.log('Permissions ready signal received:', isReady);

            if (isReady) {
                const assignedPermissions = this.permissionService.getAll();
                console.log('Assigned permissions:', assignedPermissions);

                const filteredMenu = this.filterMenuByPermissions(this.defaultMenu, assignedPermissions);
                console.log('Filtered menu:', filteredMenu);

                this.menuItems.next(filteredMenu);
            }
        });

        // Also check if permissions are already loaded (for cases where service loads after permissions are set)
        if (this.permissionService.arePermissionsLoaded()) {
            console.log('Permissions already loaded, building menu immediately');
            const assignedPermissions = this.permissionService.getAll();
            const filteredMenu = this.filterMenuByPermissions(this.defaultMenu, assignedPermissions);
            this.menuItems.next(filteredMenu);
        }
    }

    // Public method to refresh menu (can be called manually if needed)
    refreshMenu(): void {
        console.log('Manually refreshing menu');
        if (this.permissionService.arePermissionsLoaded()) {
            const assignedPermissions = this.permissionService.getAll();
            const filteredMenu = this.filterMenuByPermissions(this.defaultMenu, assignedPermissions);
            this.menuItems.next(filteredMenu);
        } else {
            // Try to reload from storage
            if (this.permissionService.reloadFromStorage()) {
                const assignedPermissions = this.permissionService.getAll();
                const filteredMenu = this.filterMenuByPermissions(this.defaultMenu, assignedPermissions);
                this.menuItems.next(filteredMenu);
            }
        }
    }

    private initializeDefaultMenu() {
        this.defaultMenu = [
            {
                name: 'Dashboard',
                type: 'link',
                icon: 'fas fa-briefcase',
                state: '/dashboard',
                permission: PermissionNames.ADMIN_DASHBOARD
            },
            {
                name: 'Prospects',
                type: 'link',
                icon: 'fa-solid fa-people-arrows',
                state: '/propsects/list',
                permission: PermissionNames.ADMIN_PROSPECTS_MANAGEMENT,
                // sub: [
                //     // {
                //     //     icon: 'fa fa-plus-circle',
                //     //     name: 'Add Tenant',
                //     //     state: '/tenant/create-tenant',
                //     //     type: 'link',
                //     //     permission: PermissionNames.ADMIN_TENANT_MANAGEMENT_CREATE
                //     // },
                //     {
                //         icon: 'fa fa-tasks',
                //         name: 'All Prospects',
                //         state: '/propsects/list',
                //         type: 'link',
                //         permission: PermissionNames.ADMIN_PROSPECTS_MANAGEMENT_LIST
                //     },
                // ]
            },
            {
                name: 'Tenants',
                type: 'dropDown',
                icon: 'fas fa-tasks',
                permission: PermissionNames.ADMIN_TENANT_MANAGEMENT,
                sub: [
                    {
                        icon: 'fa fa-tasks',
                        name: 'Tenant list',
                        state: '/tenant/tenant-list',
                        type: 'link',
                        permission: PermissionNames.ADMIN_TENANT_MANAGEMENT_VIEW
                    },
                ]
            },
            {
                name: 'Subscription',
                type: 'dropDown',
                icon: 'fa fa-credit-card',
                permission: PermissionNames.ADMIN_SUBSCRIPTION_MANAGEMENT,
                sub: [
                    {
                        icon: 'fa-solid fa-money-bill',
                        name: 'Subscription plans',
                        state: '/subscriptions/subsciption-list',
                        type: 'link',
                        permission: PermissionNames.ADMIN_SUBSCRIPTION_MANAGEMENT_PLANS
                    },
                    {
                        icon: 'fa fa-tasks',
                        name: 'Subscriptions list',
                        state: '/subscriptions/pos-subsciption',
                        type: 'link',
                        permission: PermissionNames.ADMIN_SUBSCRIPTION_MANAGEMENT_WORQUA
                    },
                ]
            },
            {
                name: 'Features',
                type: 'link',
                icon: 'fa-solid fa-tags',
                state: '/subscriptions/features',
                permission: PermissionNames.ADMIN_SUBSCRIPTION_MANAGEMENT_PLANS
            },
                        {
                name: 'Payments',
                type: 'link',
                icon: 'fa-solid fa-money-check-dollar',
                state: '/subscriptions/payment',
                permission: PermissionNames.ADMIN_PAYMENT_MANAGEMENT
            },


            {
                name: 'Invoices',
                type: 'link',
                icon: 'fa-solid fa-receipt',
                state: '/subscriptions/invoice-payment',
                permission: PermissionNames.ADMIN_PAYMENT_MANAGEMENT
            },
            {
                name: 'Email',
                type: 'dropDown',
                icon: 'fas fa-envelope',
                // permission: PermissionNames.ADMIN_EMAIL_MANAGEMENT,
                sub: [
                    {
                        icon: 'fa fa-plus-circle',
                        name: 'Email log',
                        state: '/setting/email-log',
                        type: 'link',
                        // permission: PermissionNames.ADMIN_EMAIL_MANAGEMENT_LOGS
                    },
                ]
            },
                
            {
                name: 'Dashboard',
                type: 'link',
                icon: 'fas fa-chart-line',
                state: '/environment-config',
                permission: PermissionNames.ADMIN_ENVIRONMENT_MANAGEMENT
            },
            {
                name: 'Environments',
                type: 'link',
                icon: 'fas fa-server',
                state: '/environment-config/manage',
                permission: PermissionNames.ADMIN_ENVIRONMENT_MANAGEMENT
            },
            {
                name: 'Settings',
                type: 'dropDown',
                icon: 'fa fa-cog',
                permission: PermissionNames.ADMIN_SETTINGS_MANAGEMENT,
                sub: [
                    {
                        icon: 'fa fa-tasks',
                        name: 'Setting list',
                        state: '/setting/setting-list',
                        type: 'link',
                        permission: PermissionNames.ADMIN_SETTINGS_MANAGEMENT_LIST
                    },
                    {
                        icon: 'fa-solid fa-file-signature',
                        name: 'Permissions',
                        state: '/setting/permission-list',
                        type: 'link',
                        permission: PermissionNames.ADMIN_SETTINGS_MANAGEMENT_PERMISSIONS
                    },
                    {
                        icon: 'fa-solid fa-building-circle-arrow-right',
                        name: 'Roles list',
                        state: '/setting/roles-list',
                        type: 'link',
                        permission: PermissionNames.ADMIN_ROLES_VIEW
                    },
                    {
                        icon: 'fa-solid fa-user',
                        name: 'User list',
                        state: '/setting/user-list',
                        type: 'link',
                        permission: PermissionNames.ADMIN_USER_VIEW
                    },
                    {
                        icon: 'fa-solid fa-users-rectangle',
                        name: 'Tenant users',
                        state: '/setting/tenantUser-list',
                        type: 'link',
                        permission: PermissionNames.ADMIN_SETTINGS_MANAGEMENT_TENANT_USERS
                    },
                ]
            },

        ];
    }

    filterMenuByPermissions(menuItems: IMenuItem[], userPermissions: string[]): IMenuItem[] {
        return menuItems.filter(item => {
            if (!item.permission) return false;

            const hasPermission = userPermissions.includes(item.permission);
            if (item.sub && hasPermission) {
                item.sub = this.filterMenuByPermissions(item.sub as IMenuItem[], userPermissions) as IChildItem[];
                if (item.sub.length === 1) { item.type = 'link'; item.state = item.sub[0].state; }
                return item.sub.length > 0;
            }

            return hasPermission;
        });
    }
}
